import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  createContext
} from 'react';
import deepmerge from 'deepmerge';
import { useTranslation } from 'react-i18next';

import {
  findCommonStrings,
  getCookie,
  setCookie,
  getBaseLocale,
  isVariantLocale,
  Logger,
  getCountryCode,
  getCountryName,
  isValidCountryCode
} from 'wtb-utils';
import { saveLanguageToBE } from 'api';
import { useAppContext, useBillingContext, useTransactionContext } from 'context';
import { locales, defaultLocale } from 'locale';
import getDeviceLocale from 'locale/getDeviceLocale';

const LocaleContext = createContext();

const languages = Object.keys(locales);
const localeExists = (locale) => (!!locales[locale]);
const getLocaleName = (locale) => (localeExists(locale) ? locales[locale].name : null);
const getLocaleFlag = (locale) => (localeExists(locale) ? locales[locale].flag : null);

const getTermsLanguages = (terms) => {
  const content = (terms && terms.map)
    ? terms.map((item) => item.content)
    : [];

  const extendedLanguages = (terms && terms.map)
    ? content.map((item) => Object.keys(item)).flat()
    : [];
  const summaryLanguages = (terms && terms.map)
    ? Object.keys(terms[0].content)
    : [];

  return findCommonStrings([ ...extendedLanguages ], summaryLanguages);
};

const addVariantToTerms = (lang, terms) => {
  const base = getBaseLocale(lang);

  // Disabled eslint in these lines because they are used to prevent mutation of function params but is exactly
  // what we want to do in this specific case
  terms.forEach((section) => {

    section.title[lang] = section.title[base];

    section.content[lang] = section.content[base];
  });
};

// In case there's a variant language on the merchant files that WTB terms doesn't handle (because it's not needed),
// we add that variant language to WTB terms with the same content as base language so the variant language is available
const parseWtbTerms = (wtbTerms, merchantLanguages) => {
  const result = [ ...wtbTerms ];

  merchantLanguages.forEach((lang) => {
    // If not variant but yes base language, add variant with base content
    // if not 'no-x-charity' but yes 'no', add 'no-x-charity': ${'no' content}
    if (!result[0].title[lang] && result[0].title[getBaseLocale(lang)]) {
      addVariantToTerms(lang, result);
    }
  });

  return result;
};

const getAvailableLanguages = (merchantTerms, wtbTerms) => {
  const merchantLanguages = getTermsLanguages(merchantTerms);
  const wtbLanguages = getTermsLanguages(parseWtbTerms(wtbTerms, merchantLanguages));

  const common = findCommonStrings(merchantLanguages, wtbLanguages, languages);

  if (common?.length === 0) {
    return [ defaultLocale ];
  } else {
    return common;
  }
};

const defaultState = {
  locale: undefined,
  availableLanguages: undefined
};

const compareArrays = (array1, array2) => {
  // if the other array is a falsy value, return
  if (!array1 || !array2) { return false; }

  // compare lengths - can save a lot of time
  if (array1.length !== array2.length) { return false; }

  for (let i = 0, l = array1.length; i < l; i++) {
    // Check if we have nested arrays
    if (array1[i] instanceof Array && array2[i] instanceof Array) {
      // recurse into the nested arrays
      if (!array1[i].equals(array2[i])) { return false; }
    } else if (array1[i] !== array2[i]) {
      // Warning - two different object instances will never be equal: {x:20} != {x:20}
      return false;
    }
  }

  return true;
};

const LocaleProvider = ({ children, initialState: state = {} }) => {
  const initialState = deepmerge(defaultState, state);

  const { isError, isDownload } = useAppContext();
  const { setBilling } = useBillingContext();
  const { transaction, merchant, wtbTerms } = useTransactionContext();

  const [ currentLocale, setCurrentLocale ] = useState(initialState.locale);
  const [ baseLocale, setBaseLocale ] = useState(initialState.locale);
  const [ availableLanguages, setAvailableLanguages ] = useState(initialState.availableLanguages);

  const { i18n, t: translate } = useTranslation();

  const isLocaleAvailable = useCallback((locale) => {
    if (!availableLanguages || !locale || !localeExists(locale)) return false;

    return availableLanguages.includes(locale);
  }, [ availableLanguages ]);

  const changeLocale = useCallback((locale) => {
    if (!isLocaleAvailable(locale) || currentLocale === locale) return;

    setCookie('locale', locale, {
      domain: process.env.REACT_APP_LOCALE_COOKIE_DOMAIN,
      path: '/',
      samesite: 'none',
      secure: true
    });
    i18n.changeLanguage(locale);
    setCurrentLocale(locale);

    // On first load currentLocale is undefined, so it would be always sent to BE even if locale is the same
    // To avoid that, we compare with transaction.language in that case
    const hasToSendToBE = currentLocale
      ? locale !== currentLocale
      : locale !== transaction.language;

    if (transaction.id && hasToSendToBE) {
      saveLanguageToBE(transaction.id, locale);
    }

    setBilling((prev) => {
      if (!prev?.country) return prev;

      // billing.country is stored as a localized name, so it needs to be converted to its code before translating it
      const countryCode = isValidCountryCode(prev.country) ? prev.country : getCountryCode(prev.country);
      const country = getCountryName(countryCode, locale);

      return country ? { ...prev, country } : prev;
    });
  }, [
    isLocaleAvailable,
    currentLocale,
    i18n,
    transaction.id,
    transaction.language,
    setBilling
  ]);

  const setDefaultLocale = useCallback(() => {
    const deviceLocale = getDeviceLocale(availableLanguages);

    if (deviceLocale) {
      changeLocale(deviceLocale);
    } else if (availableLanguages?.length) {
      changeLocale(availableLanguages[0]);
    } else if (isLocaleAvailable(defaultLocale)) {
      changeLocale(defaultLocale);
    } else {
      Logger.warn('No default language could be set.');
    }
  }, [ availableLanguages, changeLocale, isLocaleAvailable ]);

  useEffect(() => {
    if (!availableLanguages) {
      if (isDownload()) {
        setAvailableLanguages(languages);
        return;
      } else if (isError()) {
        const nonVariants = [];
        const savedLocale = getCookie('locale')?.value;

        languages.forEach((lang) => {
          if (!isVariantLocale(lang) || lang === savedLocale) {
            nonVariants.push(lang);
          }
        });

        setAvailableLanguages(nonVariants);
      }
    }

    if (!merchant?.terms || !wtbTerms) return;

    const _available = getAvailableLanguages(merchant.terms, wtbTerms);

    if (!compareArrays(_available, availableLanguages)) {
      setAvailableLanguages(_available);
    }
  }, [ availableLanguages, merchant?.terms, wtbTerms, transaction.language, isError, isDownload ]);

  // Change language automatically after transaction.language is updated (if available).
  // If not, change locale to stored one if exists and is available or set the default one
  useEffect(() => {
    if (!availableLanguages || currentLocale || (!isError() && !transaction.language)) return;

    const cookieLanguage = getCookie('locale');

    if (cookieLanguage?.value && isLocaleAvailable(cookieLanguage.value)) {
      changeLocale(cookieLanguage.value);
    } else if (transaction.language && isLocaleAvailable(transaction.language)) {
      changeLocale(transaction.language);
    } else {
      setDefaultLocale();
    }
  }, [
    transaction,
    currentLocale,
    availableLanguages,
    isError,
    changeLocale,
    isLocaleAvailable,
    setDefaultLocale
  ]);

  useEffect(() => {
    if (!currentLocale) return;

    setBaseLocale(getBaseLocale(currentLocale));
  }, [ currentLocale ]);

  return (
    <LocaleContext.Provider
      value={{
        locale: currentLocale,
        baseLocale,
        locales: languages,
        getLocaleName,
        getLocaleFlag,
        isLocaleAvailable,
        changeLocale,
        setDefaultLocale,
        translate,
        availableLanguages
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
};

const LocaleConsumer = LocaleContext.Consumer;

const useLocaleContext = () => {
  const context = useContext(LocaleContext);

  if (context === undefined) {
    throw new Error('useLocaleContext must be used within a LocaleProvider');
  }

  return context;
};

export {
  LocaleContext, LocaleProvider, LocaleConsumer, useLocaleContext
};
